import { PropsWithChildren } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import styled from "styled-components";
import { ArrayControl, getUniqueSelectOptions, translate, SelectOption } from "@shape/reef-shared";
import SelectWithFilter from "components/SelectWithFilter";
import { SelectWithFilterValue } from "components/SelectWithFilter/types";
import { BaseField, BaseFieldProps } from "./BaseField";
import { ReadOnlyField } from "./ReadOnlyField";

const Container = styled.div`
  width: 100%;
  transition: 0.2s ease;
  display: flex;
  flex-direction: column;

  > div {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    width: 100%;
    min-height: 36px;
    position: relative;
    animation: showRow 0.2s ease-out;

    &:not(:last-child) {
      padding-bottom: 12px;
    }

    .field {
      width: 100%;
      margin-bottom: 0;

      &:not(:first-child) {
        margin-left: 12px;
      }
    }
  }

  @keyframes showRow {
    0% {
      opacity: 0;
      top: -12px;
      max-height: 0px;
    }
    100% {
      opacity: 1;
      top: 0px;
      max-height: 60px;
    }
  }
`;

interface FieldProps {
  id: string;
  rowValue: SelectWithFilterValue;
}

type onChangeType = (selectedValues: SelectWithFilterValue[], fields: FieldProps[]) => void;

type formOnChangeType = (newValues: FieldProps[]) => void;

export interface FieldRenderProps {
  onChange: formOnChangeType;
  value: FieldProps[];
}

export interface MultipleSelectRowsFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends BaseFieldProps<TFieldValues, TName> {
  placeholder?: string;
  options: SelectOption[];
  isLoading?: boolean;
  onChange?: onChangeType;
  returnValueKey?: keyof SelectOption;
  onFilterChange?: (currentFilter: string) => void;
  maxResults?: number;
  defaultValue?: string[] | null;
  allowEmpty?: boolean;
  initialIndexAllowedToEdit?: number;
  renderSideLabel?: (index: number) => JSX.Element | null;
  isUnique?: boolean;
  isAssessment?: boolean;
}

const getAppendValue = (value: SelectWithFilterValue = "") => ({
  id: (() => String(new Date().getTime() + Math.floor(Math.random() * 1000000000)))(),
  rowValue: value,
});

const setNewData = (
  newFields: FieldProps[],
  formOnChange: formOnChangeType,
  onChange?: onChangeType
) => {
  formOnChange(newFields);
  onChange?.(
    newFields.map(({ rowValue }) => rowValue),
    newFields
  );
};

const append = (
  fields: FieldProps[],
  newField: Partial<Record<string, any>>,
  formOnChange: formOnChangeType,
  onChange?: onChangeType
) => {
  const newFields = [...fields, newField as FieldProps];
  setNewData(newFields, formOnChange, onChange);
};

const update = (
  fields: FieldProps[],
  newValue: SelectWithFilterValue,
  index: number,
  formOnChange: formOnChangeType,
  onChange?: onChangeType
) => {
  const newFields = [...fields];
  newFields[index] = {
    ...fields[index],
    rowValue: newValue,
  };
  setNewData(newFields, formOnChange, onChange);
};

const remove = (
  fields: FieldProps[],
  index: number,
  formOnChange: formOnChangeType,
  onChange?: onChangeType
) => {
  const newFields = fields.filter((_, removeIndex) => removeIndex !== index);
  setNewData(newFields, formOnChange, onChange);
};

export function MultipleSelectRowsField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  rules,
  label,
  defaultValue,
  placeholder,
  options,
  isLoading,
  onChange,
  form,
  readOnly,
  returnValueKey,
  onFilterChange,
  maxResults,
  allowEmpty,
  initialIndexAllowedToEdit,
  renderSideLabel,
  isUnique = true,
  isAssessment,
}: PropsWithChildren<MultipleSelectRowsFieldProps<TFieldValues, TName>>) {
  let defaultErrorMessage =
    rules?.required && typeof rules?.required === "string" ? rules?.required : undefined;
  defaultErrorMessage =
    !defaultErrorMessage && rules?.required === true
      ? translate("formGeneric.requiredAtLeastOneItem")
      : defaultErrorMessage;

  return readOnly ? (
    <ReadOnlyField
      label={label}
      content={
        defaultValue?.length ? defaultValue.map((value) => <div key={value}>{value}</div>) : "-"
      }
      multiple
    />
  ) : (
    <BaseField
      form={form}
      name={name}
      label={label}
      defaultValue={
        defaultValue?.length
          ? defaultValue.map((value) => getAppendValue(value))
          : [getAppendValue()]
      }
      rules={{
        setValueAs: (values: FieldProps[]) =>
          values.reduce<SelectWithFilterValue[]>(
            (result, current) => (current.rowValue ? [...result, current.rowValue] : result),
            []
          ),
        ...(defaultErrorMessage
          ? {
              validate: (values: FieldProps[]) => {
                if (
                  (!initialIndexAllowedToEdit && !values.find((value) => value.rowValue)) ||
                  (initialIndexAllowedToEdit &&
                    !values.reduce(
                      (result, current, index) =>
                        index < initialIndexAllowedToEdit ? result && !!current.rowValue : result,
                      true
                    ))
                ) {
                  return defaultErrorMessage;
                }
                return undefined;
              },
            }
          : {}),
      }}
      fieldClassName="multiple-select-rows-field"
      render={({ field }) => (
        <Container>
          {field.value.map((item: FieldProps, index: number) => {
            const uniqueOptions = isUnique
              ? getUniqueSelectOptions(options, field.value, index, returnValueKey)
              : options;
            return (
              <div key={item.id}>
                {renderSideLabel?.(index)}
                <SelectWithFilter
                  key={item.id}
                  options={uniqueOptions}
                  isLoading={isLoading}
                  onChange={(newValue: SelectWithFilterValue) => {
                    update(field.value, newValue, index, field.onChange, onChange);
                  }}
                  onFilterChange={onFilterChange}
                  returnValueKey={returnValueKey}
                  maxResults={maxResults}
                  placeholder={placeholder}
                  readOnly={readOnly}
                  selectedValue={field.value[index].rowValue}
                />
                <ArrayControl
                  remove={(indexToRemove) => {
                    remove(field.value, indexToRemove, field.onChange, onChange);
                  }}
                  fieldsLength={field.value.length}
                  index={index}
                  isLoading={isLoading}
                  readOnly={readOnly}
                  allowEmpty={allowEmpty}
                  initialIndexAllowedToEdit={initialIndexAllowedToEdit}
                />
              </div>
            );
          })}
        </Container>
      )}
    />
  );
}
